import React, { useMemo, useState } from 'react';
import { FiEdit2, FiPlay, FiTrash2 } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useLibrary } from '../hooks/useLibrary';
import { useAudioPlayer } from '../audio/AudioPlayerContext';
import { AudioFormat } from '../types';
import { formatDuration, relativeTime } from '../utils/format';
import AdSlot from '../components/AdSlot';
import '../pages/pages.css';
import './LibraryPage.css';

type Filter = 'all' | AudioFormat;

export default function LibraryPage() {
  const [filter, setFilter] = useState<Filter>('all');
  const { items, loading, error, rename, remove } = useLibrary(filter === 'all' ? undefined : filter);
  const player = useAudioPlayer();
  const navigate = useNavigate();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValue, setEditValue] = useState('');
  const [search, setSearch] = useState('');

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q ? items.filter((it) => (it.title || '').toLowerCase().includes(q)) : items;
  }, [items, search]);

  const playItem = (item: (typeof items)[number]) => {
    if (!item.documentId) return;
    player.play({
      id: item.id,
      title: item.title || (item.format === 'audiobook' ? 'Audiobook' : 'Podcast'),
      documentId: item.documentId,
      format: item.format,
      durationSeconds: item.durationSeconds,
    });
  };

  const startEdit = (id: number, current?: string) => {
    setEditingId(id);
    setEditValue(current || '');
  };

  const commitEdit = async (id: number) => {
    const v = editValue.trim();
    if (v) await rename(id, v);
    setEditingId(null);
  };

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">Library</h1>
          <p className="page__subtitle">Your generated audio, available on any device.</p>
        </div>
        <button className="btn btn--primary btn--sm" onClick={() => navigate('/create')}>
          + New
        </button>
      </div>

      <div className="lib-toolbar">
        <div className="lib-filters">
          {(['all', 'podcast', 'audiobook'] as Filter[]).map((f) => (
            <button
              key={f}
              className={`lib-filter${filter === f ? ' lib-filter--active' : ''}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'podcast' ? 'Podcasts' : 'Audiobooks'}
            </button>
          ))}
        </div>
        <input
          className="input lib-search"
          placeholder="Search…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <AdSlot format="banner" label="Sponsored" />

      {error && <div className="doc-error">{error}</div>}

      {loading ? (
        <div className="loading-block">
          <span className="spinner" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="empty">
          <span className="empty__emoji">🎧</span>
          <p className="empty__title">Nothing here yet</p>
          <p className="muted">Generate a podcast or audiobook and it will show up here.</p>
          <button className="btn btn--primary" onClick={() => navigate('/create')}>
            Create audio
          </button>
        </div>
      ) : (
        <div className="lib-list">
          {filtered.map((item, idx) => {
            const active = player.current?.id === item.id;
            return (
              <React.Fragment key={item.id}>
                {idx > 0 && idx % 8 === 0 && <AdSlot format="banner" label="Sponsored" />}
                <div className={`lib-card${active ? ' lib-card--active' : ''}`}>
                  <button className="lib-card__play" onClick={() => playItem(item)} aria-label="Play">
                    <FiPlay />
                  </button>
                  <div className="lib-card__body">
                    {editingId === item.id ? (
                      <input
                        className="input lib-card__edit"
                        value={editValue}
                        autoFocus
                        onChange={(e) => setEditValue(e.target.value)}
                        onBlur={() => commitEdit(item.id)}
                        onKeyDown={(e) => e.key === 'Enter' && commitEdit(item.id)}
                      />
                    ) : (
                      <span className="lib-card__title" title={item.title}>
                        {item.title || 'Untitled'}
                      </span>
                    )}
                    <div className="lib-card__meta">
                      <span className="pill pill--muted">{item.format === 'audiobook' ? '📖 Audiobook' : '🎙️ Podcast'}</span>
                      <span className="muted">{formatDuration(item.durationSeconds)}</span>
                      <span className="muted">{relativeTime(item.createdAt)}</span>
                    </div>
                  </div>
                  <div className="lib-card__actions">
                    <button className="icon-btn" onClick={() => startEdit(item.id, item.title)} aria-label="Rename">
                      <FiEdit2 />
                    </button>
                    <button
                      className="icon-btn icon-btn--danger"
                      onClick={() => {
                        if (window.confirm('Delete this from your library?')) remove(item.id);
                      }}
                      aria-label="Delete"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>
              </React.Fragment>
            );
          })}
        </div>
      )}
    </div>
  );
}
